/**
 * Dots currency: balance sync, run rewards, daily login.
 */
import type { AccountDataPayloadV1 } from "../lib/accountDataTypes";
import type { ModeDotsConfigDto, ShopCosmeticDto } from "../lib/cloud";
import {
  THEME_COSMETIC_EQUIP_SLOT_KEYS,
  type ThemeCosmeticChannelKey,
  type ThemeCosmeticEquipSlot,
} from "../../lib/shopTypes";
import {
  type EquippedThemeChannels,
  setEquippedThemeChannelsState,
} from "./theme-cosmetic";

export type { EquippedThemeChannels, ThemeCosmeticChannelKey, ThemeCosmeticEquipSlot };

export type DotsRunReward = {
  dotsFromScore: number;
  bonusDots: number;
  totalDots: number;
  isNewRecord: boolean;
  isFirstRun: boolean;
};

export type DotsState = {
  balance: number;
  dailyStreak: number;
  lastLoginDate: string | null;
  ownedCosmeticIds: string[];
  claimedCrateIds: string[];
  equippedCosmeticIds: string[];
  equippedThemeChannels: EquippedThemeChannels;
  outfits: Array<{
    id: string;
    name: string;
    equippedCosmeticIds: string[];
    cursorColor?: string;
    trailColor?: string;
  }>;
};

const DEFAULT_CONFIG: ModeDotsConfigDto = {
  modeId: "normal",
  dotsConversionRate: 100,
  dotsBonusBase: 50,
};

let modeDotsConfig: ModeDotsConfigDto[] = [];
let dotsState: DotsState = {
  balance: 0,
  dailyStreak: 0,
  lastLoginDate: null,
  ownedCosmeticIds: [],
  claimedCrateIds: [],
  equippedCosmeticIds: [],
  equippedThemeChannels: {},
  outfits: [],
};

let lastRunReward: DotsRunReward | null = null;

/** Bumped on every local dots mutation so in-flight account pulls can skip stale snapshots. */
let dotsMutationGen = 0;

function bumpDotsMutationGen() {
  dotsMutationGen += 1;
}

export function getDotsMutationGen(): number {
  return dotsMutationGen;
}

function normalizeEquippedIds(dots: {
  equippedCosmeticIds?: unknown;
  equippedCosmeticId?: unknown;
} | null | undefined): string[] {
  if (Array.isArray(dots?.equippedCosmeticIds)) {
    return [
      ...new Set(
        dots.equippedCosmeticIds.filter((x): x is string => typeof x === "string" && x.length > 0),
      ),
    ];
  }
  if (typeof dots?.equippedCosmeticId === "string" && dots.equippedCosmeticId.length > 0) {
    return [dots.equippedCosmeticId];
  }
  return [];
}

function normalizeThemeChannels(raw: unknown): EquippedThemeChannels {
  if (!raw || typeof raw !== "object") return {};
  const o = raw as Record<string, unknown>;
  const out: EquippedThemeChannels = {};
  for (const key of THEME_COSMETIC_EQUIP_SLOT_KEYS) {
    const id = o[key];
    if (typeof id === "string" && id.length > 0) out[key] = id;
  }
  return out;
}

function syncThemeChannelsRuntime(channels: EquippedThemeChannels) {
  setEquippedThemeChannelsState(channels);
}

export function setModeDotsConfig(items: ModeDotsConfigDto[]) {
  modeDotsConfig = items.slice();
}

export function getModeDotsConfig(modeId: string): ModeDotsConfigDto {
  return modeDotsConfig.find((m) => m.modeId === modeId) ?? { ...DEFAULT_CONFIG, modeId };
}

function normalizeOutfits(raw: unknown): DotsState["outfits"] {
  if (!Array.isArray(raw)) return [];
  const out: DotsState["outfits"] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const o = row as Record<string, unknown>;
    const id = typeof o.id === "string" ? o.id.trim() : "";
    if (!id) continue;
    const name = typeof o.name === "string" ? o.name.trim().slice(0, 40) : "";
    const equippedCosmeticIds = Array.isArray(o.equippedCosmeticIds)
      ? [
          ...new Set(
            o.equippedCosmeticIds.filter((x): x is string => typeof x === "string" && x.length > 0),
          ),
        ]
      : [];
    const cursorColor =
      typeof o.cursorColor === "string" && /^#[0-9a-fA-F]{6}$/i.test(o.cursorColor.trim())
        ? o.cursorColor.trim().toLowerCase()
        : undefined;
    const trailColor =
      typeof o.trailColor === "string" && /^#[0-9a-fA-F]{6}$/i.test(o.trailColor.trim())
        ? o.trailColor.trim().toLowerCase()
        : undefined;
    out.push({
      id,
      name,
      equippedCosmeticIds,
      ...(cursorColor ? { cursorColor } : {}),
      ...(trailColor ? { trailColor } : {}),
    });
    if (out.length >= 24) break;
  }
  return out;
}

export function applyDotsFromAccountPayload(payload: AccountDataPayloadV1 | null | undefined) {
  const d = payload?.dots;
  const equippedThemeChannels = normalizeThemeChannels(d?.equippedThemeChannels);
  dotsState = {
    balance: Math.max(0, Math.floor(Number(d?.balance) || 0)),
    dailyStreak: Math.max(0, Math.floor(Number(d?.dailyStreak) || 0)),
    lastLoginDate: typeof d?.lastLoginDate === "string" ? d.lastLoginDate : null,
    ownedCosmeticIds: Array.isArray(d?.ownedCosmeticIds)
      ? d.ownedCosmeticIds.filter((x): x is string => typeof x === "string")
      : [],
    claimedCrateIds: Array.isArray(d?.claimedCrateIds)
      ? d.claimedCrateIds.filter((x): x is string => typeof x === "string")
      : [],
    equippedCosmeticIds: normalizeEquippedIds(d),
    equippedThemeChannels,
    outfits: normalizeOutfits(d?.outfits),
  };
  bumpDotsMutationGen();
  syncThemeChannelsRuntime(equippedThemeChannels);
}

export function applyDotsFromApi(dots: {
  balance: number;
  dailyStreak: number;
  ownedCosmeticIds: string[];
  claimedCrateIds?: string[];
  equippedCosmeticIds?: string[];
  equippedCosmeticId?: string | null;
  equippedThemeChannels?: EquippedThemeChannels | null;
  lastLoginDate?: string | null;
  outfits?: unknown;
} | null | undefined) {
  if (!dots) return;
  const equippedThemeChannels =
    dots.equippedThemeChannels !== undefined
      ? normalizeThemeChannels(dots.equippedThemeChannels)
      : dotsState.equippedThemeChannels;
  dotsState = {
    balance: Math.max(0, Math.floor(Number(dots.balance) || 0)),
    dailyStreak: Math.max(0, Math.floor(Number(dots.dailyStreak) || 0)),
    lastLoginDate: typeof dots.lastLoginDate === "string" ? dots.lastLoginDate : dotsState.lastLoginDate,
    ownedCosmeticIds: Array.isArray(dots.ownedCosmeticIds) ? [...dots.ownedCosmeticIds] : [],
    claimedCrateIds: Array.isArray(dots.claimedCrateIds) ? [...dots.claimedCrateIds] : dotsState.claimedCrateIds,
    equippedCosmeticIds: normalizeEquippedIds(dots),
    equippedThemeChannels,
    outfits: dots.outfits !== undefined ? normalizeOutfits(dots.outfits) : dotsState.outfits,
  };
  bumpDotsMutationGen();
  syncThemeChannelsRuntime(equippedThemeChannels);
}

export function getDotsState(): DotsState {
  return {
    ...dotsState,
    ownedCosmeticIds: [...dotsState.ownedCosmeticIds],
    claimedCrateIds: [...dotsState.claimedCrateIds],
    equippedCosmeticIds: [...dotsState.equippedCosmeticIds],
    equippedThemeChannels: { ...dotsState.equippedThemeChannels },
    outfits: dotsState.outfits.map((o) => ({
      ...o,
      equippedCosmeticIds: [...o.equippedCosmeticIds],
    })),
  };
}

export function getDotsBalance(): number {
  return dotsState.balance;
}

export function buildDotsAccountSlice(): NonNullable<AccountDataPayloadV1["dots"]> {
  return {
    balance: dotsState.balance,
    dailyStreak: dotsState.dailyStreak,
    lastLoginDate: dotsState.lastLoginDate,
    ownedCosmeticIds: [...dotsState.ownedCosmeticIds],
    claimedCrateIds: [...dotsState.claimedCrateIds],
    equippedCosmeticIds: [...dotsState.equippedCosmeticIds],
    equippedCosmeticId: dotsState.equippedCosmeticIds[0] ?? null,
    equippedThemeChannels: { ...dotsState.equippedThemeChannels },
    outfits: dotsState.outfits.map((o) => ({
      ...o,
      equippedCosmeticIds: [...o.equippedCosmeticIds],
    })),
  };
}

export function setOutfits(outfits: DotsState["outfits"]) {
  dotsState.outfits = normalizeOutfits(outfits);
}

export function ownsShopCosmetic(id: string): boolean {
  return dotsState.ownedCosmeticIds.includes(id);
}

export function isCosmeticEquipped(id: string): boolean {
  if (dotsState.equippedCosmeticIds.includes(id)) return true;
  return THEME_COSMETIC_EQUIP_SLOT_KEYS.some((k) => dotsState.equippedThemeChannels[k] === id);
}

export function setEquippedCosmeticIds(ids: string[]) {
  dotsState.equippedCosmeticIds = [...new Set(ids.filter((x) => typeof x === "string" && x.length > 0))];
}

export function setEquippedThemeChannels(channels: EquippedThemeChannels) {
  dotsState.equippedThemeChannels = normalizeThemeChannels(channels);
  syncThemeChannelsRuntime(dotsState.equippedThemeChannels);
}

export function getEquippedThemeChannels(): EquippedThemeChannels {
  return { ...dotsState.equippedThemeChannels };
}

/** True when any slot of this Theme cosmetic is currently applied. */
export function isThemeChannelEquipped(
  cosmeticId: string,
  channel?: ThemeCosmeticEquipSlot,
): boolean {
  if (channel) return dotsState.equippedThemeChannels[channel] === cosmeticId;
  return THEME_COSMETIC_EQUIP_SLOT_KEYS.some((k) => dotsState.equippedThemeChannels[k] === cosmeticId);
}

/** @deprecated prefer setEquippedCosmeticIds */
export function setEquippedCosmeticId(id: string | null) {
  dotsState.equippedCosmeticIds = id ? [id] : [];
}

export function addOwnedCosmetic(id: string) {
  if (!dotsState.ownedCosmeticIds.includes(id)) {
    dotsState.ownedCosmeticIds.push(id);
  }
}

export function adjustDotsBalance(delta: number) {
  dotsState.balance = Math.max(0, dotsState.balance + Math.floor(Number(delta) || 0));
  bumpDotsMutationGen();
}

export function setDotsBalance(balance: number) {
  dotsState.balance = Math.max(0, Math.floor(Number(balance) || 0));
  bumpDotsMutationGen();
}

export function computeRunDotsReward(
  modeId: string,
  scoreFinal: number,
  previousRecord: number,
  isNewRecord: boolean,
): DotsRunReward {
  const cfg = getModeDotsConfig(modeId);
  const rate = Math.max(0, Math.floor(Number(cfg.dotsConversionRate) || 0));
  const dotsFromScore = rate <= 0 ? 0 : Math.floor(Math.max(0, scoreFinal) / rate);
  const isFirstRun = previousRecord <= 0;
  let bonusDots = 0;
  if (isNewRecord) {
    const base = Math.max(0, cfg.dotsBonusBase);
    if (isFirstRun) {
      bonusDots = base;
    } else if (previousRecord > 0) {
      const improvement = (scoreFinal - previousRecord) / previousRecord;
      bonusDots = Math.floor(base * Math.max(0, improvement));
      bonusDots = Math.min(bonusDots, base);
    }
  }
  const totalDots = dotsFromScore + bonusDots;
  return { dotsFromScore, bonusDots, totalDots, isNewRecord, isFirstRun };
}

export function awardRunDots(reward: DotsRunReward): number {
  if (reward.totalDots <= 0) return 0;
  adjustDotsBalance(reward.totalDots);
  lastRunReward = reward;
  return reward.totalDots;
}

export function setLastRunDotsReward(reward: DotsRunReward | null) {
  lastRunReward = reward;
}

export function getLastRunDotsReward(): DotsRunReward | null {
  return lastRunReward;
}

export function clearLastRunDotsReward() {
  lastRunReward = null;
}

export function canAffordCosmetic(item: ShopCosmeticDto): boolean {
  if (item.unlockType !== "dots") return false;
  const price = Math.max(0, Math.floor(Number(item.priceDots) || 0));
  return dotsState.balance >= price;
}

export function cosmeticActionLabel(item: ShopCosmeticDto): "BUY" | "Claim" {
  return item.unlockType === "dots" ? "BUY" : "Claim";
}
